import React, { useCallback, useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Send, Loader2, CheckCircle2, AlertTriangle, Mail } from "lucide-react";
import { toast } from "sonner";

const fmtINR = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(Number(n) || 0);

/**
 * Bulk-email payslips for a given period.
 * Props:
 *  - open / onOpenChange
 *  - period_month (YYYY-MM)
 *  - propertyId (optional scope)
 *  - rows: array of payslip rows in the current Payroll view (used as candidate list)
 *  - staff: array of user objects to look up email by user_id
 *  - onSent: callback after a successful send
 */
export default function BulkPayslipEmailDialog({
  open, onOpenChange, period_month, propertyId, rows = [], staff = [], onSent,
}) {
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const [senders, setSenders] = useState([]);
  const [senderEmail, setSenderEmail] = useState("");
  const [cc, setCc] = useState("");
  const [selected, setSelected] = useState({}); // user_id -> bool
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null); // {total,sent,failed,results}
  const [confirmOpen, setConfirmOpen] = useState(false);

  const staffById = useMemo(() => {
    const m = {};
    (staff || []).forEach((s) => { m[s.id] = s; });
    return m;
  }, [staff]);

  // Candidates = rows (payslips) for this period intersected with staff (email)
  const candidates = useMemo(() => {
    return (rows || []).map((r) => {
      const u = staffById[r.user_id] || {};
      return {
        user_id: r.user_id,
        user_name: r.user_name || u.name,
        email: u.email,
        net_payable: r.net_payable,
        employee_id: u.employee_id,
        role: u.role,
      };
    });
  }, [rows, staffById]);

  const load = useCallback(async () => {
    try {
      const [t, s] = await Promise.all([
        api.get("/email/templates?category=payslip"),
        api.get("/email/senders").catch(() => ({ data: [] })),
      ]);
      setTemplates(t.data || []);
      const def = (t.data || []).find((x) => x.key === "default_payslip") || (t.data || [])[0];
      if (def) setTemplateId(def.id);
      setSenders(s.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load templates / senders");
    }
  }, []);

  useEffect(() => {
    if (!open) return;
    load();
    setResults(null);
    setCc("");
    setSenderEmail("");
    // Default: all candidates with an email are selected
    const next = {};
    candidates.forEach((c) => { if (c.email) next[c.user_id] = true; });
    setSelected(next);
  }, [open, candidates, load]);

  const selectedIds = useMemo(() => Object.keys(selected).filter((k) => selected[k]), [selected]);
  const withoutEmail = candidates.filter((c) => !c.email).length;

  const toggle = (uid) => setSelected((s) => ({ ...s, [uid]: !s[uid] }));
  const toggleAll = (v) => {
    const next = {};
    candidates.forEach((c) => { if (c.email) next[c.user_id] = v; });
    setSelected(next);
  };

  const send = async () => {
    setConfirmOpen(false);
    if (selectedIds.length === 0) { toast.error("Pick at least one recipient"); return; }
    setBusy(true);
    setResults(null);
    try {
      const payload = {
        period_month,
        property_id: propertyId || null,
        user_ids: selectedIds,
        template_id: templateId || null,
        sender_email: senderEmail || null,
        cc: cc.split(",").map((s) => s.trim()).filter(Boolean),
      };
      const { data } = await api.post("/payslips/bulk-email", payload);
      setResults(data);
      const msg = `Sent ${data.sent} of ${data.total}${data.failed ? ` · ${data.failed} failed` : ""}`;
      if (data.failed === 0 && data.sent > 0) toast.success(msg);
      else if (data.sent > 0) toast.warning(msg);
      else toast.error(msg);
      if (onSent) onSent(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Bulk send failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-3xl" data-testid="bulk-payslip-email-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Mail className="w-4 h-4" /> Bulk-email payslips · {period_month}</DialogTitle>
            <DialogDescription>
              Sends each selected employee a copy of their <strong>{period_month}</strong> payslip PDF from the HR mailbox. Every send is logged in Email Logs.
            </DialogDescription>
          </DialogHeader>

          {!results ? (
            <div className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Template</Label>
                  <Select value={templateId} onValueChange={setTemplateId}>
                    <SelectTrigger data-testid="bulk-payslip-template"><SelectValue placeholder="Pick a template" /></SelectTrigger>
                    <SelectContent>
                      {templates.map((t) => (
                        <SelectItem key={t.id} value={t.id}>{t.name}{t.is_default ? " (default)" : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">From mailbox</Label>
                  <Select value={senderEmail || "auto"} onValueChange={(v) => setSenderEmail(v === "auto" ? "" : v)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">Use HR default (Email Settings)</SelectItem>
                      {senders.map((s) => <SelectItem key={s.sender_email} value={s.sender_email}>{s.sender_email}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label className="text-xs">Cc (optional — copies every send)</Label>
                <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="finance@happyretreats.in" data-testid="bulk-payslip-cc" />
              </div>

              <div className="flex items-center justify-between text-xs text-zinc-600 border-y border-zinc-200 py-2 mt-2">
                <div>
                  <strong>{selectedIds.length}</strong> of <strong>{candidates.length}</strong> selected
                  {withoutEmail > 0 && <span className="text-amber-700 ml-2">· {withoutEmail} skipped (no email)</span>}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => toggleAll(true)} className="text-zinc-900 underline" data-testid="bulk-payslip-select-all">Select all</button>
                  <span className="text-zinc-300">·</span>
                  <button onClick={() => toggleAll(false)} className="text-zinc-900 underline" data-testid="bulk-payslip-select-none">None</button>
                </div>
              </div>

              <div className="border border-zinc-200 rounded-md max-h-72 overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-zinc-50 border-b border-zinc-200 sticky top-0">
                    <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-widest">
                      <th className="py-2 px-3 w-10"></th>
                      <th className="py-2 px-3">Employee</th>
                      <th className="py-2 px-3">Email</th>
                      <th className="py-2 px-3 text-right">Net payable</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidates.length === 0 && (
                      <tr><td colSpan={4} className="py-8 text-center text-zinc-400">No payslips exist for {period_month} yet — create or copy from previous first.</td></tr>
                    )}
                    {candidates.map((c) => (
                      <tr key={c.user_id} className="border-b border-zinc-100 last:border-0" data-testid={`bulk-payslip-row-${c.user_id}`}>
                        <td className="py-2 px-3">
                          <input
                            type="checkbox"
                            checked={!!selected[c.user_id]}
                            disabled={!c.email}
                            onChange={() => toggle(c.user_id)}
                            data-testid={`bulk-payslip-check-${c.user_id}`}
                          />
                        </td>
                        <td className="py-2 px-3">
                          <div className="font-medium">{c.user_name}</div>
                          <div className="text-[10px] text-zinc-500 font-mono">{c.employee_id || "—"} · {(c.role || "").replace("_", " ")}</div>
                        </td>
                        <td className="py-2 px-3 text-xs text-zinc-700">
                          {c.email || <span className="text-amber-700">No email on file</span>}
                        </td>
                        <td className="py-2 px-3 text-right font-mono text-xs">{fmtINR(c.net_payable)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <ResultsView results={results} />
          )}

          <DialogFooter>
            {!results ? (
              <>
                <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>Cancel</Button>
                <Button
                  onClick={() => setConfirmOpen(true)}
                  disabled={busy || selectedIds.length === 0 || !templateId}
                  className="bg-zinc-950 hover:bg-zinc-800"
                  data-testid="bulk-payslip-send"
                >
                  {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />}
                  Send {selectedIds.length} payslip{selectedIds.length === 1 ? "" : "s"}
                </Button>
              </>
            ) : (
              <Button onClick={() => onOpenChange(false)} className="bg-zinc-950 hover:bg-zinc-800" data-testid="bulk-payslip-close">Close</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send {selectedIds.length} payslip{selectedIds.length === 1 ? "" : "s"}?</AlertDialogTitle>
            <AlertDialogDescription>
              Each selected employee will receive an email with their <strong>{period_month}</strong> payslip PDF attached. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={send} className="bg-zinc-950 hover:bg-zinc-800" data-testid="bulk-payslip-confirm">
              Yes, send them
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function ResultsView({ results }) {
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-3 gap-3">
        <Card label="Total" value={results.total} />
        <Card label="Sent" value={results.sent} accent="green" />
        <Card label="Failed" value={results.failed} accent={results.failed ? "red" : "neutral"} />
      </div>
      <div className="border border-zinc-200 rounded-md max-h-72 overflow-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 border-b border-zinc-200 sticky top-0">
            <tr className="text-left text-zinc-500 uppercase text-[10px] tracking-widest">
              <th className="py-2 px-3">Status</th>
              <th className="py-2 px-3">Employee</th>
              <th className="py-2 px-3">Email</th>
              <th className="py-2 px-3">Note</th>
            </tr>
          </thead>
          <tbody>
            {results.results.map((r) => {
              const ok = r.status === "sent";
              const Icon = ok ? CheckCircle2 : AlertTriangle;
              const color = ok ? "text-emerald-700" : "text-red-700";
              return (
                <tr key={r.user_id} className="border-b border-zinc-100 last:border-0">
                  <td className="py-2 px-3">
                    <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest ${color}`}>
                      <Icon className="w-3 h-3" /> {r.status}
                    </span>
                  </td>
                  <td className="py-2 px-3 font-medium">{r.user_name}</td>
                  <td className="py-2 px-3 text-xs">{r.email || "—"}</td>
                  <td className="py-2 px-3 text-xs text-zinc-600">{r.error || (ok ? "Sent · attached PDF" : "—")}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Card({ label, value, accent }) {
  const palette = {
    green: "bg-emerald-50 text-emerald-900 border-emerald-200",
    red: "bg-red-50 text-red-900 border-red-200",
    neutral: "bg-white",
  };
  return (
    <div className={`border border-zinc-200 rounded-md p-3 ${palette[accent || "neutral"] || palette.neutral}`}>
      <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-2xl font-bold font-display mt-0.5">{value}</div>
    </div>
  );
}
